<script setup lang="ts">
import Simplebar from "simplebar-vue";
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";

import type { TableReportDto } from "@/components/datascience/TableReport.vue";
import { smartToFixed } from "@/services/utils";

const { spec } = defineProps<{
  spec: TableReportDto;
}>();
const emit = defineEmits<{
  selectColumnIndex: [columnIndex: number];
}>();

const tableWrapper = useTemplateRef<HTMLDivElement>("table-wrapper");
const selectedColumnIndex = ref(-1);
const selectedColumnOverlayX = ref("0");
const selectedColumnOverlayWidth = ref("0");

function calculateOverlayFromIndex(columnIndex: number) {
  if (!tableWrapper.value || columnIndex === -1) {
    selectedColumnOverlayX.value = "0";
    selectedColumnOverlayWidth.value = "0";
    return;
  }

  // Find a cell in the selected column (use first data row)
  const cell = tableWrapper.value.querySelector<HTMLTableCellElement>(
    `tbody tr:first-child td:nth-child(${columnIndex + 2})`
  );

  if (cell) {
    const wrapperRect = tableWrapper.value.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    selectedColumnOverlayX.value = `${cellRect.left - wrapperRect.left}px`;
    selectedColumnOverlayWidth.value = `${cellRect.width}px`;

    // Ensure the selected column is visible in the scroll viewport
    if (!tableWrapper.value || selectedColumnIndex.value === -1) {
      return;
    }

    const stickyColumn = tableWrapper.value.querySelector<HTMLTableCellElement>(
      "tbody tr:first-child td:first-child"
    );
    const scrollContainer = tableWrapper.value.closest<HTMLElement>(".simplebar-content-wrapper");

    if (!cell || !scrollContainer) {
      return;
    }

    const stickyColumnWidth = stickyColumn?.getBoundingClientRect().width ?? 0;
    const scrollContainerRect = scrollContainer.getBoundingClientRect();

    // Calculate visible area accounting for sticky column
    const visibleLeft = scrollContainerRect.left + stickyColumnWidth;
    const visibleRight = scrollContainerRect.right;

    // Check if cell is outside visible area
    if (cellRect.left < visibleLeft) {
      // Cell is hidden behind sticky column, scroll left
      const scrollOffset = cellRect.left - visibleLeft;
      scrollContainer.scrollLeft += scrollOffset;
    } else if (cellRect.right > visibleRight) {
      // Cell is beyond right edge, scroll right
      const scrollOffset = cellRect.right - visibleRight;
      scrollContainer.scrollLeft += scrollOffset;
    }
  }
}

function selectColumn(e: MouseEvent) {
  const target = e.target as HTMLElement | null;
  if (target) {
    const cell = target.closest<HTMLTableCellElement>("td, th");
    if (cell) {
      const columnIndex = cell.cellIndex - 1;
      selectedColumnIndex.value = columnIndex;
      emit("selectColumnIndex", columnIndex);
      calculateOverlayFromIndex(columnIndex);
    }
  }
}

function selectPreviousColumn() {
  if (selectedColumnIndex.value > 0) {
    const newIndex = Math.max(0, selectedColumnIndex.value - 1);
    selectedColumnIndex.value = newIndex;
    emit("selectColumnIndex", newIndex);
    calculateOverlayFromIndex(newIndex);
  }
}

function selectNextColumn() {
  if (selectedColumnIndex.value < spec.n_columns - 1) {
    const newIndex = selectedColumnIndex.value + 1;
    selectedColumnIndex.value = newIndex;
    emit("selectColumnIndex", newIndex);
    calculateOverlayFromIndex(newIndex);
  }
}

function selectColumnIndex(columnIndex: number) {
  selectedColumnIndex.value = columnIndex;
  emit("selectColumnIndex", columnIndex);
  calculateOverlayFromIndex(columnIndex);
}

async function redrawSelection() {
  await nextTick();
  calculateOverlayFromIndex(selectedColumnIndex.value);
}

const resizeObserver = new ResizeObserver(async () => {
  await redrawSelection();
});

watch(() => spec, redrawSelection);

watch(selectedColumnIndex, (newIndex) => {
  calculateOverlayFromIndex(newIndex);
});

onMounted(() => {
  if (tableWrapper.value) {
    resizeObserver.observe(tableWrapper.value);
  }
});

onBeforeUnmount(() => {
  if (tableWrapper.value) {
    resizeObserver.unobserve(tableWrapper.value);
  }
});

defineExpose({
  selectColumnIndex,
});
</script>

<template>
  <div class="table">
    <Simplebar class="table-scroll">
      <div
        class="table-wrapper"
        ref="table-wrapper"
        :class="{ 'has-selection': selectedColumnIndex !== -1 }"
        :style="{
          '--selected-column-overlay-x': selectedColumnOverlayX,
          '--selected-column-overlay-width': selectedColumnOverlayWidth,
        }"
      >
        <table
          @keydown.left.prevent="selectPreviousColumn"
          @keydown.right.prevent="selectNextColumn"
          tabindex="2"
        >
          <thead>
            <tr>
              <th class="text-base-medium"></th>
              <th
                class="text-base-medium"
                v-for="(col, i) of spec.extract_head.columns"
                :key="i"
                @click="selectColumn"
              >
                {{ col }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in spec.extract_head.data" :key="i">
              <td class="text-base-medium">{{ i }}</td>
              <td
                v-for="(value, j) in row"
                :key="j"
                @click="selectColumn"
                :class="{ selected: selectedColumnIndex === j }"
              >
                <span v-if="Number.isFinite(value)" class="number">
                  {{ smartToFixed(value) }}
                </span>
                <span v-else class="truncate">{{ value }}</span>
              </td>
            </tr>
            <tr>
              <td class="text-base-medium">#</td>
              <td
                v-for="(value, j) in spec.columns"
                :key="j"
                @click="selectColumn"
                :class="{ selected: selectedColumnIndex === j }"
              >
                ...
              </td>
            </tr>
            <tr v-for="(row, i) in spec.extract_tail.data" :key="i">
              <td class="text-base-medium">
                {{ spec.n_rows - (spec.extract_tail.data.length - i) }}
              </td>
              <td
                v-for="(value, j) in row"
                :key="j"
                @click="selectColumn"
                :class="{ selected: selectedColumnIndex === j }"
              >
                <span v-if="Number.isFinite(value)" class="number">
                  {{ smartToFixed(value) }}
                </span>
                <span v-else class="truncate">{{ value }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Simplebar>
    <div class="text-base-medium">{{ spec.n_rows }} rows x {{ spec.n_columns }} columns</div>
  </div>
</template>

<style scoped>
.table {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-8);

  .table-scroll {
    overflow-x: scroll;

    /* column selection overlay */
    .table-wrapper {
      position: relative;

      &.has-selection::after {
        position: absolute;
        top: 0;
        bottom: 0;
        left: var(--selected-column-overlay-x);
        width: var(--selected-column-overlay-width);
        height: 100%;
        border: var(--stroke-width-md) var(--border-brand) solid;
        content: "";
        pointer-events: none;
        transition:
          left var(--animation-duration) var(--animation-easing),
          width var(--animation-duration) var(--animation-easing);
      }
    }

    table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      table-layout: auto;

      &,
      &:focus,
      &:focus-visible {
        outline: none;
      }

      thead {
        tr {
          color: var(--content-secondary);

          th {
            z-index: 1;
            padding: var(--numbers-8);
            border-width: var(--stroke-width-md);
            border-color: var(--border-tertiary);
            border-top-style: solid;
            background-color: var(--background-tertiary);
            cursor: pointer;
            text-align: right;
            white-space: preserve nowrap;

            &:first-child {
              position: sticky;
              z-index: 2;
              left: 0;
              cursor: inherit;
            }
          }

          &:last-child th {
            border-bottom-style: solid;
          }

          &:first-child th:first-child {
            border-left-style: solid;
            border-top-left-radius: var(--numbers-8);
          }

          &:first-child th:last-child {
            border-right-style: solid;
            border-top-right-radius: var(--numbers-8);
          }
        }
      }

      tbody {
        tr {
          td {
            padding: var(--numbers-8);
            border: var(--stroke-width-md) none var(--border-tertiary);
            border-bottom-style: solid;
            border-left-style: solid;
            background-color: var(--background-primary);
            cursor: pointer;
            text-align: right;
            transition: color var(--animation-duration) var(--animation-easing);

            &:first-child {
              position: sticky;
              left: 0;
              background-color: var(--background-tertiary);
              cursor: inherit;
              font-family: GeistMono, monospace;
            }

            &:last-child {
              border-right-style: solid;
            }

            &.selected {
              color: var(--content-brand);
            }

            .truncate {
              display: inline-block;
              overflow: hidden;
              max-width: 200px;
              text-overflow: ellipsis;
              vertical-align: middle;
              white-space: nowrap;
            }
          }

          &:last-child td {
            border-bottom-style: solid;
          }

          &:last-child td:first-child {
            border-bottom-left-radius: var(--numbers-8);
          }

          &:last-child td:last-child {
            border-bottom-right-radius: var(--numbers-8);
          }
        }
      }
    }
  }
}
</style>
